<template>
    <div>
        <form-row label="Thema" label-for="subject" error-name="subject" required>
            <form-input name="subject" v-model="subject"/>
        </form-row>
        <form-row label="Empfänger" label-for="recipients" error-name="recipients" required>
            <form-select name="recipients" :options="recipients" v-model="selectedRecipients" multiple>
                <template #all>Alle {{typeName}} ausgewählt</template>
                <template #multiple="{count}">{{count}} {{typeName}} ausgewählt</template>
            </form-select>
            <p v-if="showHint"><strong>Hinweis</strong>: Der gewünschte Nutzer wurde deaktiviert oder gelöscht. Sie können jedoch andere Empfänger aus der Liste wählen.</p>
        </form-row>
        <dl class="form-row">
            <dt class="form-label"><label>Besuchte Seite</label></dt>
            <dd class="form-element" v-text="url"></dd>
        </dl>
        <form-row label="Nachricht" label-for="message" error-name="message" required>
            <form-textarea rows="10" class="long" v-model="message"/>
        </form-row>
        <form-row label="Nachricht als Kopie erhalten" label-for="send_copy" class="form-row-checkbox" element-first>
            <form-input-checkbox name="send_copy" v-model="sendCopy"/>
        </form-row>
    </div>
</template>

<script setup lang="ts">
    import {usePortuna} from '@/composables/portuna';
    import {formSubmit} from '@/library/form';
    import {popupDefaultCloseButton} from '@/library/popup';
    import {deepCopyObject, errorHandler} from '@/utils/portuna';
    import type {TinyEmitter} from 'tiny-emitter';
    import {computed, ref, inject} from 'vue';
    import {useRouter} from 'vue-router';
    import {useStore} from 'vuex';
    import Loader from '../library/intranet/loader';
    import TopMessageHandler from '../library/topMessageHandler';
    import FormInput from './form/FormInput.vue';
    import FormInputCheckbox from './form/FormInputCheckbox.vue';
    import FormRow from './form/FormRow.vue';
    import FormSelect from './form/FormSelect.vue';
    import FormTextarea from './form/FormTextarea.vue';

    const portuna = usePortuna();

    defineExpose(portuna);

    const popupEmitter = inject<TinyEmitter>('popup-emitter');

    const props = withDefaults(
        defineProps<{
            id?: number | number[];
            type?: 'user' | 'employee';
            recipients?: [];
            prefillSubject?: string;
            prefillMessage?: string;
        }>(),
        {
            id: 0,
            type: 'user',
            recipients: () => [],
            prefillSubject: '',
            prefillMessage: '',
        },
    );

    const store              = useStore();
    const router             = useRouter();
    const subject            = ref<string>('');
    const message            = ref<string>('');
    const url                = ref<string>('');
    const sendCopy           = ref<boolean>(true);
    const selectedRecipients = ref<number[]>([]);
    const typeName           = computed(() =>
    {
        if (props.type === 'user')
        {
            return 'Nutzer';
        }
        else if (props.type === 'employee')
        {
            return 'Mitarbeiter';
        }
        return 'Unbekannt';
    });

    const showHint = computed(() =>
    {
        if (Array.isArray(props.id))
        {
            return false;
        }
        let exists = store.getters.exists(props.id, props.type);
        if (exists)
        {
            return !store.state[props.type + 's'][props.id].active;
        }
        return exists === false;
    });

    const submit = () =>
    {
        let data = {
            recipients: selectedRecipients.value,
            subject: subject.value,
            message: message.value,
            url: url.value,
            send_copy: sendCopy.value,
        };

        Loader.show();

        formSubmit('/ajax/contact-form/type/' + props.type, {ignoreScroll: true}, data)
        .then(() =>
        {
            TopMessageHandler.success('Ihre Nachricht wurde verschickt.');
            popupEmitter.emit('close');
        })
        .catch(errorHandler)
        .finally(() => Loader.hide());
    };

    url.value                = window.location.origin + router.currentRoute.value.fullPath;
    subject.value            = props.prefillSubject;
    message.value            = props.prefillMessage;
    selectedRecipients.value = Array.isArray(props.id) ? deepCopyObject(props.id) : [props.id];

    popupEmitter.emit('setButtons', [
        popupDefaultCloseButton,
        {
            title: 'Abschicken',
            type: 'primary',
            icon: 'check',
            cmd: () => submit(),
        },
    ] as PopupButton[]);
</script>